// src/pages/EquipePage.tsx
import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { api } from "../services/api";
import Swal from "sweetalert2";

interface Equipe {
  id?: number;
  nome: string;
  ativo: boolean;
}

const EquipePage: React.FC = () => {
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState<Equipe | null>(null);

  const [nome, setNome] = useState("");

  /* ------------------------------------
      CARREGAR EQUIPES
  ------------------------------------ */
  const loadEquipes = async () => {
    try {
      setLoading(true);
      const res = await api.get<Equipe[]>("/equipes");
      setEquipes(res.data || []);
    } catch {
      Swal.fire("Erro", "Falha ao carregar equipes.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEquipes();
  }, []);

  /* ------------------------------------
      ABRIR / FECHAR MODAL
  ------------------------------------ */
  const abrirModalAdd = () => {
    setEditando(null);
    setNome("");
    setShowModal(true);
  };

  const abrirModalEditar = (equipe: Equipe) => {
    setEditando(equipe);
    setNome(equipe.nome);
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setNome("");
    setEditando(null);
  };

  /* ------------------------------------
      SALVAR EQUIPE
  ------------------------------------ */
  const salvarEquipe = async () => {
    if (nome.trim().length < 2)
      return Swal.fire("Atenção", "O nome deve ter pelo menos 2 caracteres.", "warning");

    try {
      if (editando) {
        await api.put(`/equipes/${editando.id}`, {
          ...editando,
          nome: nome.trim(),
        });
        Swal.fire("Sucesso", "Equipe atualizada!", "success");
      } else {
        await api.post("/equipes", {
          nome: nome.trim(),
          ativo: true,
        });
        Swal.fire("Sucesso", "Equipe cadastrada!", "success");
      }

      fecharModal();
      loadEquipes();
    } catch (err) {
      Swal.fire("Erro", "Falha ao salvar equipe.", "error");
    }
  };

  /* ------------------------------------
      ATIVAR / DESATIVAR
  ------------------------------------ */
  const toggleAtivo = async (equipe: Equipe) => {
    try {
      await api.patch(`/equipes/${equipe.id}/ativo`, {
        ativo: !equipe.ativo,
      });

      Swal.fire(
        "Pronto!",
        `Equipe ${!equipe.ativo ? "ativada" : "desativada"} com sucesso.`,
        "success"
      );

      loadEquipes();
    } catch {
      Swal.fire("Erro", "Falha ao atualizar equipe.", "error");
    }
  };

  /* ------------------------------------
      RENDER
  ------------------------------------ */
  return (
    <div className="d-flex">
      <Sidebar />

      <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
        <div className="d-flex justify-content-between mb-4">
          <h2>👥 Equipes</h2>

          <button className="btn btn-primary" onClick={abrirModalAdd}>
            Nova Equipe
          </button>
        </div>

        {/* LISTA */}
        <div className="card shadow-sm p-3">
          {loading && <p className="text-muted">Carregando...</p>}

          {!loading && equipes.length === 0 && (
            <p className="text-muted">Nenhuma equipe cadastrada.</p>
          )}

          {!loading && equipes.length > 0 && (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th className="text-center">Status</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {equipes.map((e) => (
                    <tr key={e.id}>
                      <td>{e.nome}</td>

                      <td className="text-center">
                        {e.ativo ? (
                          <span className="badge bg-success">Ativo</span>
                        ) : (
                          <span className="badge bg-secondary">Inativo</span>
                        )}
                      </td>

                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => abrirModalEditar(e)}
                        >
                          Editar
                        </button>

                        <button
                          className={`btn btn-sm ${
                            e.ativo ? "btn-outline-danger" : "btn-outline-success"
                          }`}
                          onClick={() => toggleAtivo(e)}
                        >
                          {e.ativo ? "Desativar" : "Ativar"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* MODAL */}
        {showModal && (
          <div
            className="modal fade show"
            style={{
              display: "block",
              background: "rgba(0,0,0,0.45)",
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">
                    {editando ? "Editar Equipe" : "Nova Equipe"}
                  </h5>
                  <button className="btn-close" onClick={fecharModal}></button>
                </div>

                <div className="modal-body">
                  <label className="form-label">Nome da Equipe</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex: Pastel, Churrasco, Rifa..."
                  />
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={fecharModal}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={salvarEquipe}>
                    {editando ? "Atualizar" : "Salvar"}
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EquipePage;
